import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    FaLightbulb,
    FaChartLine,
    FaFileAlt,
    FaHandshake,
    FaUserTie,
    FaMoneyBillWave,
    FaCheckCircle,
    FaBalanceScale,
    FaGlobe,
    FaCalculator,
    FaCheck,
    FaShieldAlt,
    FaUsers,
    FaBuilding,
    FaCogs
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const FeatureCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: ${props => props.bgColor || 'linear-gradient(to right, #228B22, #E60000)'};
  color: white;
  font-size: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const FeatureTitle = styled.h4`
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
  font-size: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-size: 16px;
  color: #555;
  
  svg {
    color: #228B22;
    margin-right: 10px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const NestedFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 15px;
  color: #555;
  margin-left: 25px;
  
  &:before {
    content: "○";
    color: #E60000;
    margin-right: 10px;
    flex-shrink: 0;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
  }
  
  svg {
    font-size: 40px;
    margin-bottom: 15px;
  }
  
  p {
    font-weight: bold;
    margin-top: 10px;
    font-size: 18px;
  }
`;

const SectionTitle = styled.h3`
  position: relative;
  font-weight: bold;
  margin-bottom: 40px;
  padding-bottom: 15px;
  text-align: center;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #228B22, #E60000);
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  margin: 40px auto 0 auto;
  text-align: center;
  width: 50%;
  display: block;

  @media (max-width: 768px) {
    width: 90%;
  }

  h4 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  button {
    background: white;
    color: #333;
    border: none;
    padding: 10px 25px;
    border-radius: 6px;
    font-weight: bold;
    transition: all 0.3s ease;
    margin-top: 15px;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
  }
`;

const ServiceSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 5px;
    background: linear-gradient(to bottom, #228B22, #E60000);
  }
`;

const ApproachCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    border-color: linear-gradient(to right, #228B22, #E60000);
  }
`;

const PmeEtiGrandesEntreprises = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const section = document.getElementById("approach-section");
            if (section) {
                const rect = section.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container text-white text-start">
                    <h1 className="fw-bold">PME, ETI et Grandes Entreprises</h1>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/" className="text-white text-decoration-none">
                                    Accueil
                                </Link>{" "}
                                /{" "}
                                <Link to="/services" className="text-white text-decoration-none">
                                    Services
                                </Link>
                            </li>
                            <li className="breadcrumb-item active text-white" aria-current="page">
                                PME, ETI et Grandes Entreprises
                            </li>
                        </ol>
                    </nav>
                </div>
            </section>

            <section className="service-content py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 mx-auto text-center mb-5">
                            <h2 className="fw-bold">EXPERTISE COMPTABLE, AUDIT ET CONSEIL : ACCOMPAGNEMENT SUR MESURE POUR PME, ETI ET GRANDES ENTREPRISES</h2>
                            <h3 className="fw-bold mt-3">De la Performance Opérationnelle à la Stratégie de Croissance : Votre Partenaire de Confiance</h3>
                            <div className="separator mx-auto my-3"></div>
                            <p className="lead text-muted">
                                Quelle que soit la taille de votre entreprise — PME en développement, ETI en croissance, ou Grande Entreprise aux enjeux complexes — CINEF-EC vous apporte une expertise comptable, financière, juridique et stratégique de haut niveau. Nous adaptons nos services à vos besoins spécifiques, à votre secteur d'activité et à vos ambitions, pour vous aider à optimiser votre performance, maîtriser vos risques et atteindre vos objectifs.
                                <br /><br />
                                Nous vous proposons une gamme complète de services, modulables et évolutifs, pour répondre à vos enjeux à chaque étape de votre développement.
                            </p>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                                    <FaBuilding />
                                </FeatureIcon>
                                <FeatureTitle>Solutions adaptées à chaque entreprise</FeatureTitle>
                                <p>
                                    Nos services s'adaptent à la taille et aux besoins spécifiques de votre entreprise, qu'il s'agisse d'une PME, d'une ETI ou d'une Grande Entreprise, pour vous accompagner efficacement.
                                </p>
                            </FeatureCard>
                        </div>
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                                    <FaHandshake />
                                </FeatureIcon>
                                <FeatureTitle>Partenaire de confiance</FeatureTitle>
                                <p>
                                    Nous sommes bien plus qu'un cabinet d'expertise comptable : nous sommes votre
                                    partenaire stratégique qui vous accompagne dans tous les aspects de votre développement.
                                </p>
                            </FeatureCard>
                        </div>
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                                    <FaUserTie />
                                </FeatureIcon>
                                <FeatureTitle>Expertise pluridisciplinaire</FeatureTitle>
                                <p>
                                    Notre équipe réunit des experts dans tous les domaines nécessaires à la réussite
                                    de votre entreprise : comptabilité, fiscalité, droit, gestion, finance et stratégie.
                                </p>
                            </FeatureCard>
                        </div>
                        <div className="col-md-6">
                            <FeatureCard>
                                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                                    <FaChartLine />
                                </FeatureIcon>
                                <FeatureTitle>Vision stratégique</FeatureTitle>
                                <p>
                                    Nous vous aidons à concevoir et mettre en œuvre des stratégies de développement
                                    adaptées à votre entreprise, pour une croissance durable et maîtrisée.
                                </p>
                            </FeatureCard>
                        </div>
                    </div>
                </div>
            </section>

            <section className="services-section py-5 bg-light">
                <div className="container">
                    <SectionTitle>Nos Services</SectionTitle>

                    <ServiceSection className="mb-4">
                        <FeatureTitle>I. Expertise Comptable et Financière</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Tenue, Surveillance et Révision Comptable</strong>
                                    </FeatureItem>
                                    <NestedFeatureItem>
                                        PME: Externalisation totale ou partielle de la fonction comptable, révision des comptes, assistance à la mise en place de procédures
                                    </NestedFeatureItem>
                                    <NestedFeatureItem>
                                        ETI: Supervision de la comptabilité tenue en interne, révision des comptes, consolidation (si groupe), assistance à l'optimisation des processus comptables
                                    </NestedFeatureItem>
                                    <NestedFeatureItem>
                                        Grandes Entreprises: Missions d'audit contractuel, revue de processus, assistance à la mise en conformité, consolidation
                                    </NestedFeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Établissement des Comptes Annuels et des Liasses Fiscales</strong> : Production des états financiers et des déclarations fiscales, en conformité avec les réglementations en vigueur
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Déclarations Fiscales (tous régimes et taxes)</strong> : Prise en charge complète de vos obligations déclaratives
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Optimisation Fiscale</strong> : Analyse de votre situation fiscale, recherche d'optimisations (crédits d'impôt, exonérations, etc.), conseil en structuration fiscale
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Assistance en cas de Contrôle Fiscal</strong> : Préparation, accompagnement et défense de vos intérêts
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </ServiceSection>

                    <ServiceSection className="mb-4">
                        <FeatureTitle>II. Gestion Sociale et Paie</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Gestion de la Paie (externalisée ou en mode SaaS)</strong> : Établissement des bulletins de paie, déclarations sociales, gestion des entrées/sorties, etc.
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Conseil en Droit Social</strong> : Accompagnement sur toutes les questions relatives au droit du travail, contrats, ruptures de contrat, relations avec les IRP, etc.
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Audit Social</strong> : Vérification de la conformité de vos pratiques sociales
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Optimisation de la Rémunération</strong> : Mise en place de dispositifs d'intéressement, de participation, d'épargne salariale, etc.
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </ServiceSection>

                    <ServiceSection className="mb-4">
                        <FeatureTitle>III. Conseil en Gestion, Finance et Stratégie</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Pilotage de la Performance</strong>
                                    </FeatureItem>
                                    <NestedFeatureItem>
                                        Mise en place de tableaux de bord de gestion et d'indicateurs clés de performance (KPI)
                                    </NestedFeatureItem>
                                    <NestedFeatureItem>
                                        Comptabilité analytique et contrôle de gestion
                                    </NestedFeatureItem>
                                    <NestedFeatureItem>
                                        Élaboration et suivi de budgets
                                    </NestedFeatureItem>
                                    <NestedFeatureItem>
                                        Reporting financier (à destination de la direction, des actionnaires, des banques)
                                    </NestedFeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Conseil en Financement</strong> : Analyse des besoins, recherche de financements (dette, fonds propres), montage de dossiers, négociations
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Évaluation d'Entreprise</strong> : Dans le cadre de cessions, acquisitions, fusions, restructurations, etc.
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Accompagnement aux Opérations de Haut de Bilan (Corporate Finance)</strong> : M&A, levées de fonds, introduction en bourse, etc.
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Conseil en Stratégie</strong> : Analyse de l'environnement concurrentiel, identification des opportunités de croissance, accompagnement à la mise en œuvre de la stratégie
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Gestion des risques</strong>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Restructuration et retournement d'entreprises</strong>
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </ServiceSection>

                    <ServiceSection className="mb-4">
                        <FeatureTitle>IV. Conseil Juridique (en partenariat avec des avocats spécialisés)</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Droit des Sociétés</strong> : Constitution de sociétés, modifications statutaires, secrétariat juridique, opérations sur le capital, restructurations, etc.
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Droit des Affaires</strong> : Contrats commerciaux, baux commerciaux, contentieux
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Droit Fiscal</strong> : Conseil et contentieux
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </ServiceSection>

                    <ServiceSection className="mb-4">
                        <FeatureTitle>V. Audit</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Audit Légal (Commissariat aux Comptes)</strong> : Certification des comptes annuels
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Audit Contractuel</strong> : Missions d'audit spécifiques (audit d'acquisition, audit de procédures, etc.)
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </ServiceSection>

                    <ServiceSection>
                        <FeatureTitle>VI. Services Complémentaires</FeatureTitle>
                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Consolidation</strong> : Établissement des comptes consolidés pour les groupes
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Gestion de Patrimoine</strong> pour les dirigeants
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Formations</strong> : Formations sur mesure en comptabilité, finance, gestion, droit social
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                            <div className="col-md-6">
                                <FeatureList>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Mise à Disposition de Systèmes d'Information</strong>
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Externalisation de Fonctions</strong> : Prise en charge de tout ou partie de vos fonctions finance, comptabilité, paie ou administrative
                                    </FeatureItem>
                                    <FeatureItem>
                                        <FaCheck /> <strong>Accompagnement à l'international</strong>
                                    </FeatureItem>
                                </FeatureList>
                            </div>
                        </div>
                    </ServiceSection>
                </div>
            </section>

            <section className="approach-section py-5" id="approach-section">
                <div className="container">
                    <SectionTitle>Notre Approche</SectionTitle>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <ApproachCard>
                                <FaLightbulb className="text-success" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                <FeatureTitle>Écoute et Compréhension</FeatureTitle>
                                <p className="text-muted">
                                    Nous prenons le temps de comprendre vos enjeux, vos spécificités et vos objectifs
                                </p>
                            </ApproachCard>
                        </div>
                        <div className="col-md-4">
                            <ApproachCard>
                                <FaUserTie className="text-danger" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                <FeatureTitle>Expertise et Réactivité</FeatureTitle>
                                <p className="text-muted">
                                    Une équipe d'experts pluridisciplinaires et réactifs à votre service
                                </p>
                            </ApproachCard>
                        </div>
                        <div className="col-md-4">
                            <ApproachCard>
                                <FaCogs className="text-primary" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                <FeatureTitle>Solutions Sur Mesure</FeatureTitle>
                                <p className="text-muted">
                                    Des services adaptés à vos besoins spécifiques et à votre taille
                                </p>
                            </ApproachCard>
                        </div>
                        <div className="col-md-6">
                            <ApproachCard>
                                <FaHandshake className="text-success" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                <FeatureTitle>Proximité et Disponibilité</FeatureTitle>
                                <p className="text-muted">
                                    À vos côtés pour vous accompagner au quotidien dans vos défis
                                </p>
                            </ApproachCard>
                        </div>
                        <div className="col-md-6">
                            <ApproachCard>
                                <FaGlobe className="text-danger" style={{ fontSize: "40px", marginBottom: "15px" }} />
                                <FeatureTitle>Innovation et Digitalisation</FeatureTitle>
                                <p className="text-muted">
                                    Utilisation des dernières technologies pour une expérience client optimale
                                </p>
                            </ApproachCard>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-12">
                            <div className="sectors-container">
                                <SectionTitle>Solutions Adaptées à Chaque Type d'Entreprise</SectionTitle>
                            </div>
                            <div className="row g-4">
                                <div className="col-md-4">
                                    <ServiceCard>
                                        <FaUsers className="text-success" />
                                        <FeatureTitle>PME</FeatureTitle>
                                        <p className="text-muted">
                                            Externalisation totale ou partielle, optimisation des processus, accompagnement à la croissance
                                        </p>
                                    </ServiceCard>
                                </div>
                                <div className="col-md-4">
                                    <ServiceCard>
                                        <FaBuilding className="text-danger" />
                                        <FeatureTitle>ETI</FeatureTitle>
                                        <p className="text-muted">
                                            Supervision comptable, consolidation, pilotage de la performance, conseil en financement
                                        </p>
                                    </ServiceCard>
                                </div>
                                <div className="col-md-4">
                                    <ServiceCard>
                                        <FaGlobe className="text-primary" />
                                        <FeatureTitle>Grandes Entreprises</FeatureTitle>
                                        <p className="text-muted">
                                            Audit contractuel, corporate finance, gestion des risques, accompagnement à l'international
                                        </p>
                                    </ServiceCard>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ContactCard>
                        <h4>Vous recherchez un partenaire de confiance ?</h4>
                        <p>
                            Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée.
                            Nous serons ravis de discuter de vos besoins et de vous proposer des solutions adaptées.
                        </p>
                        <Link to="/contact" className="btn">Nous contacter</Link>
                    </ContactCard>
                </div>
            </section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), 
                      url("/assets/images/business.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .separator {
          height: 4px;
          width: 80px;
          background: linear-gradient(to right, #228B22, #E60000);
          border-radius: 2px;
        }
        
        .bg-light {
          background-color: #f8f9fa;
        }
        
        .lead {
          font-size: 1.2rem;
          line-height: 1.6;
        }
        
        #approach-section {
          transition: opacity 0.5s ease, transform 0.5s ease;
          opacity: ${isVisible ? 1 : 0};
          transform: ${isVisible ? 'translateY(0)' : 'translateY(20px)'};
        }
      `}</style>
        </>
    );
};

export default PmeEtiGrandesEntreprises;